import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Grid, Paper, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, TextField, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu
} from "@mui/material";
import {
  History as HistoryIcon,
  People as PeopleIcon, School as SchoolIcon,
  Class as ClassIcon, PendingActions as PendingIcon,
  EventBusy as LeaveIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon,
  FileDownload as ExportIcon
} from "@mui/icons-material";
import { fetchData, putData } from "../../BaseUrl";
import { exportToCSV } from "../../utils/tableUtils";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import Toast from "../../DynamicComponent/Toast";
import AttendanceHistoryDialog from "./AttendanceHistoryDialog";

const GRADES = [
  "All", "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"
];

const SECTIONS = ["All Sections", "Sec A", "Sec B", "Sec C"];

const TAB_LABELS = ["Overview", "Class Details", "Teachers", "Leave Approval", "Trend Report"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TREND_FALLBACK = [78, 82, 85, 80, 88, 91, 76, 83, 87, 90, 85, 88];

const statusStyle = (pct) => {
  if (pct >= 90) return { label: "Excellent", color: "#4caf50", bg: "#e8f5e9" };
  if (pct >= 75) return { label: "Good", color: "#2196f3", bg: "#e3f2fd" };
  if (pct >= 50) return { label: "Average", color: "#ff9800", bg: "#fff3e0" };
  return { label: "Poor", color: "#f44336", bg: "#fce4ec" };
};

const AdminAttendance = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedGrade, setSelectedGrade] = useState("All");
  const [selectedSection, setSelectedSection] = useState("All Sections");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [overview, setOverview] = useState({
    totalStudents: 0, totalTeachers: 0, totalClasses: 0,
    attendancePending: 0, leaveRequests: 0
  });
  const [classData, setClassData] = useState([]);
  const [students, setStudents] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [leaveActionDialog, setLeaveActionDialog] = useState({ open: false, request: null, action: "" });
  const [trendData, setTrendData] = useState(TREND_FALLBACK);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadOverview = useCallback(async () => {
    try {
      const result = await fetchData("admin/attendance/overview");
      if (result?.data) setOverview(result.data);
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load attendance overview" });
    }
  }, []);

  const loadClassData = useCallback(async () => {
    try {
      const params = selectedGrade !== "All" ? `?grade=${encodeURIComponent(selectedGrade)}` : "";
      const result = await fetchData(`admin/attendance/class${params}`);
      if (result?.data) {
        setClassData(result.data.classes || result.data || []);
        setStudents(result.data.students || []);
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load class attendance" });
    }
  }, [selectedGrade]);

  const loadLeaveRequests = useCallback(async () => {
    try {
      const result = await fetchData("admin/attendance/leave-requests");
      setLeaveRequests(result?.data || []);
    } catch (err) {
      setLeaveRequests([]);
    }
  }, []);

  const loadTrend = useCallback(async () => {
    try {
      const result = await fetchData("admin/attendance/trend");
      if (Array.isArray(result?.data) && result.data.length > 0) {
        setTrendData(result.data);
      } else {
        setTrendData(TREND_FALLBACK);
      }
    } catch (err) {
      setTrendData(TREND_FALLBACK);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadClassData(); }, [loadClassData]);
  useEffect(() => {
    if (tabValue === 3) loadLeaveRequests();
    if (tabValue === 4) loadTrend();
  }, [tabValue, loadLeaveRequests, loadTrend]);

  const handleToggleAttendance = async (student, status) => {
    try {
      await putData(`admin/attendance/toggle/${student._id || student.id}`, { status });
      setToast({ open: true, severity: "success", message: `Marked ${status} for ${student.studentName || student.name}` });
      loadClassData();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to update attendance" });
    }
  };

  const handleLeaveAction = async (request, action) => {
    try {
      const status = action === "approve" ? "approved" : "rejected";
      await putData(`admin/attendance/leave-requests/${request._id}`, { status });
      setToast({ open: true, severity: "success", message: `Leave request ${action}d successfully` });
      setLeaveActionDialog({ open: false, request: null, action: "" });
      loadLeaveRequests();
    } catch (err) {
      setToast({ open: true, severity: "error", message: `Failed to ${action} leave request` });
      setLeaveActionDialog({ open: false, request: null, action: "" });
    }
  };

  const handleExportCSV = () => {
    setExportMenuAnchor(null);
    const exportCols = [
      { label: "Name",         field: "studentName" },
      { label: "Reg No",       field: "regNo" },
      { label: "Grade",        field: "grade" },
      { label: "Section",      field: "section" },
      { label: "Total Class",  field: "totalClass" },
      { label: "Present",      field: "present" },
      { label: "Absent",       field: "absent" },
      { label: "Attendance %", field: "attendancePct" },
    ];
    const rows = students.map(s => {
      const pct = s.totalClass > 0 ? Math.round((s.present / s.totalClass) * 100) : 0;
      return {
        studentName: s.studentName || s.name || "-",
        regNo:       s.registrationNumber || s.regNo || "-",
        grade:       s.grade || "-",
        section:     s.section || "-",
        totalClass:  s.totalClass || 0,
        present:     s.present || 0,
        absent:      s.absent || 0,
        attendancePct: `${pct}%`,
      };
    });
    const filename = `attendance_${selectedGrade}_${selectedSection}_${dateFrom || "all"}_${dateTo || "all"}`;
    exportToCSV(rows, exportCols, filename);
    setToast({ open: true, severity: "success", message: "Attendance exported as CSV" });
  };

  const handleExportPDF = async () => {
    setExportMenuAnchor(null);
    try {
      const params = new URLSearchParams({ format: "pdf" });
      if (selectedGrade !== "All")           params.append("grade",   selectedGrade);
      if (selectedSection !== "All Sections") params.append("section", selectedSection);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo)   params.append("dateTo",   dateTo);
      const res = await fetchData(`admin/attendance/export?${params.toString()}`);
      const url = res?.data?.url || res?.url;
      if (url) {
        window.open(url, "_blank");
      } else {
        // fallback — export CSV instead
        handleExportCSV();
      }
    } catch {
      setToast({ open: true, severity: "warning", message: "PDF export unavailable — downloading CSV instead" });
      handleExportCSV();
    }
  };

  const kpiCards = [
    { label: "Total Students", value: overview.totalStudents, color: "#244C77", bg: "#EAF1F8", icon: <PeopleIcon /> },
    { label: "Total Teachers", value: overview.totalTeachers, color: "#2196f3", bg: "#e3f2fd", icon: <SchoolIcon /> },
    { label: "Total Classes", value: overview.totalClasses, color: "#4caf50", bg: "#e8f5e9", icon: <ClassIcon /> },
    { label: "Attendance Pending", value: overview.attendancePending, color: "#ff9800", bg: "#fff3e0", icon: <PendingIcon /> },
    { label: "Leave Requests", value: overview.leaveRequests, color: "#f44336", bg: "#fce4ec", icon: <LeaveIcon /> }
  ];

  const studentColumns = [
    { label: "Name", field: "studentName" },
    { label: "Reg No", field: "regNo" },
    { label: "Grade", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Total Class", field: "totalClass" },
    { label: "Present", field: "present" },
    { label: "Absent", field: "absent" },
    { label: "Attendance %", field: "attendancePct" },
    { label: "Status", field: "statusChip" },
    { label: "P / A", field: "toggleAttendance" },
    { label: "Action", field: "actions" }
  ];

  const studentRows = students.map((s) => {
    const pct = s.totalClass > 0 ? Math.round((s.present / s.totalClass) * 100) : 0;
    const st = statusStyle(pct);
    return {
      ...s,
      studentName: s.studentName || s.name || "-",
      regNo: s.registrationNumber || s.regNo || "-",
      grade: s.grade || "-",
      section: s.section || "-",
      totalClass: s.totalClass || 0,
      present: s.present || 0,
      absent: s.absent || 0,
      attendancePct: `${pct}%`,
      statusChip: (
        <Chip label={st.label} size="small"
          sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
      ),
      toggleAttendance: (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button size="small" variant="contained"
            onClick={() => handleToggleAttendance(s, "present")}
            sx={{ minWidth: 32, px: 1, py: 0.25, fontSize: 12, fontWeight: 700,
              backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#388e3c" } }}>
            P
          </Button>
          <Button size="small" variant="contained"
            onClick={() => handleToggleAttendance(s, "absent")}
            sx={{ minWidth: 32, px: 1, py: 0.25, fontSize: 12, fontWeight: 700,
              backgroundColor: "#f44336", "&:hover": { backgroundColor: "#d32f2f" } }}>
            A
          </Button>
        </Box>
      ),
      actions: (
        <Tooltip title="View History">
          <IconButton size="small" onClick={() => { setSelectedStudent(s); setHistoryOpen(true); }}>
            <HistoryIcon fontSize="small" sx={{ color: "#244C77" }} />
          </IconButton>
        </Tooltip>
      )
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">Pages / Attendance Management</Typography>
          <Typography variant="h5" fontWeight="bold" mt={0.5}>Attendance Management</Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            sx={{ textTransform: "none", borderColor: "#244C77", color: "#244C77", borderRadius: 2, mt: 1 }}>
            Export
          </Button>
          <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, mt: 0.5 } }}>
            <MenuItem onClick={handleExportCSV} sx={{ fontSize: 13 }}>Export as CSV (.xlsx)</MenuItem>
            <MenuItem onClick={handleExportPDF} sx={{ fontSize: 13 }}>Export as PDF</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {kpiCards.map((kpi, idx) => (
          <Grid item xs={6} sm={4} md={2.4} key={idx}>
            <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: kpi.bg, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 2, backgroundColor: kpi.color + "20",
                display: "flex", alignItems: "center", justifyContent: "center", color: kpi.color
              }}>
                {kpi.icon}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight="bold" color={kpi.color}>{kpi.value}</Typography>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        {TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
      </Tabs>

      {/* Grade Filter Pills + Section Dropdown + Date Range */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3, alignItems: "center" }}>
        {GRADES.map((g) => (
          <Chip key={g} label={g} clickable
            onClick={() => setSelectedGrade(g)}
            sx={{
              backgroundColor: selectedGrade === g ? "#244C77" : "#f5f5f5",
              color: selectedGrade === g ? "#fff" : "text.primary",
              fontWeight: selectedGrade === g ? 600 : 400,
              "&:hover": { backgroundColor: selectedGrade === g ? "#244C77" : "#e0e0e0" }
            }} />
        ))}
        {/* Section Dropdown */}
        <FormControl size="small" sx={{ minWidth: 140, ml: 1 }}>
          <InputLabel>Section</InputLabel>
          <Select value={selectedSection} label="Section"
            onChange={(e) => setSelectedSection(e.target.value)}>
            {SECTIONS.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Week view date range */}
        <TextField size="small" label="From Date" type="date"
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160, ml: 1 }} />
        <TextField size="small" label="To Date" type="date"
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <>
          {/* Section Attendance Cards */}
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>Class Attendance Overview</Typography>
          <Grid container spacing={2} mb={3}>
            {classData.map((cls, idx) => {
              const total = (cls.present || 0) + (cls.absent || 0) + (cls.leave || 0);
              const presentPct = total > 0 ? Math.round((cls.present / total) * 100) : 0;
              const absentPct = total > 0 ? Math.round((cls.absent / total) * 100) : 0;
              const leavePct = total > 0 ? Math.round((cls.leave / total) * 100) : 0;
              return (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {cls.grade} - {cls.section || "A"}
                      </Typography>
                      <Chip label={`${total} students`} size="small" sx={{ backgroundColor: "#f5f5f5" }} />
                    </Box>
                    {/* Color-coded bar */}
                    <Box sx={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", mb: 1 }}>
                      <Box sx={{ width: `${presentPct}%`, backgroundColor: "#4caf50" }} />
                      <Box sx={{ width: `${absentPct}%`, backgroundColor: "#f44336" }} />
                      <Box sx={{ width: `${leavePct}%`, backgroundColor: "#ff9800" }} />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="caption" color="#4caf50">Present: {cls.present || 0}</Typography>
                      <Typography variant="caption" color="#f44336">Absent: {cls.absent || 0}</Typography>
                      <Typography variant="caption" color="#ff9800">Leave: {cls.leave || 0}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {(tabValue === 1 || tabValue === 2) && (
        <DynamicTable columns={studentColumns} rows={studentRows} />
      )}

      {/* Leave Approval Tab */}
      {tabValue === 3 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>Leave Requests Pending Approval</Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e0e0e0" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  {["Student Name", "Grade", "From Date", "To Date", "Type", "Reason", "Actions"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No pending leave requests
                    </TableCell>
                  </TableRow>
                ) : leaveRequests.map((req, idx) => (
                  <TableRow key={req._id || idx} hover>
                    <TableCell>{req.studentName || "-"}</TableCell>
                    <TableCell>{req.grade || "-"}</TableCell>
                    <TableCell>{req.fromDate || "-"}</TableCell>
                    <TableCell>{req.toDate || "-"}</TableCell>
                    <TableCell>
                      <Chip label={req.type || "Leave"} size="small"
                        sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", fontWeight: 600 }} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>{req.reason || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Approve">
                          <IconButton size="small"
                            onClick={() => setLeaveActionDialog({ open: true, request: req, action: "approve" })}
                            sx={{ color: "#4caf50" }}>
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small"
                            onClick={() => setLeaveActionDialog({ open: true, request: req, action: "reject" })}
                            sx={{ color: "#f44336" }}>
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Trend Report Tab */}
      {tabValue === 4 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>Monthly Attendance % Trend</Typography>
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}>
            {/* Y-axis labels + bars */}
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0, height: 220 }}>
              {/* Y axis */}
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between",
                height: 200, pr: 1, minWidth: 36 }}>
                {[100, 80, 60, 40, 20, 0].map(v => (
                  <Typography key={v} variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {v}%
                  </Typography>
                ))}
              </Box>
              {/* Bars */}
              <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 1, height: 200,
                borderLeft: "2px solid #e0e0e0", borderBottom: "2px solid #e0e0e0", px: 1, pb: 0 }}>
                {trendData.map((val, idx) => (
                  <Box key={idx} sx={{ flex: 1, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: 10 }}>
                      {val}%
                    </Typography>
                    <Box sx={{
                      width: "100%",
                      height: `${(val / 100) * 180}px`,
                      backgroundColor: val >= 90 ? "#4caf50" : val >= 80 ? "#2196f3" : val >= 70 ? "#ff9800" : "#f44336",
                      borderRadius: "4px 4px 0 0",
                      transition: "height 0.3s",
                      minWidth: 20
                    }} />
                  </Box>
                ))}
              </Box>
            </Box>
            {/* X axis month labels */}
            <Box sx={{ display: "flex", ml: "44px", mt: 0.5, gap: 1 }}>
              {MONTHS.map((m, idx) => (
                <Box key={idx} sx={{ flex: 1, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{m}</Typography>
                </Box>
              ))}
            </Box>
            {/* Legend */}
            <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
              {[
                { color: "#4caf50", label: "Excellent (≥90%)" },
                { color: "#2196f3", label: "Good (≥80%)" },
                { color: "#ff9800", label: "Average (≥70%)" },
                { color: "#f44336", label: "Poor (<70%)" }
              ].map(item => (
                <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: item.color }} />
                  <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Leave Action Confirm Dialog */}
      <Dialog open={leaveActionDialog.open}
        onClose={() => setLeaveActionDialog({ open: false, request: null, action: "" })}
        maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">
          {leaveActionDialog.action === "approve" ? "Approve Leave Request" : "Reject Leave Request"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to {leaveActionDialog.action} the leave request
            {leaveActionDialog.request ? ` for ${leaveActionDialog.request.studentName}` : ""}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveActionDialog({ open: false, request: null, action: "" })}
            variant="outlined" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleLeaveAction(leaveActionDialog.request, leaveActionDialog.action)}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: leaveActionDialog.action === "approve" ? "#4caf50" : "#f44336",
              "&:hover": { backgroundColor: leaveActionDialog.action === "approve" ? "#388e3c" : "#d32f2f" }
            }}>
            {leaveActionDialog.action === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedStudent && (
        <AttendanceHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)}
          student={selectedStudent} />
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminAttendance;
